import React, { useState } from 'react';
import { Tabs } from '@mantine/core';
import type { AdminTransferAuditView, AdminTransferOrderlineSnapshot } from 'shared-models/browser';
import { formatDateTime } from '../../monitoring-page.utils';
import classes from './transfer-audit-detail.module.scss';

type Props = {
  row: AdminTransferAuditView;
};

const transferKindLabels: Record<string, string> = {
  partial_transfer: 'העברה חלקית',
  full_transfer: 'העברה מלאה',
  merge_transfer: 'העברה במיזוג',
};

const DetailField: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className={classes.detailField}>
    <span className={classes.detailLabel}>{label}</span>
    <span className={classes.detailValue}>{value ?? '—'}</span>
  </div>
);

const TransferredItemsTable: React.FC<{
  items: number[];
  sourceOrderBefore: AdminTransferOrderlineSnapshot[];
}> = ({ items, sourceOrderBefore }) => {
  const movedLines = sourceOrderBefore.filter((line) => items.includes(line.orderlineId));

  if (movedLines.length === 0) {
    return <div className={classes.emptyTab}>{'אין פריטים מועברים.'}</div>;
  }

  return (
    <table className={classes.itemsTable}>
      <thead>
        <tr>
          <th>{'פריט'}</th>
          <th>{'כמות'}</th>
          <th>{'מחיר'}</th>
        </tr>
      </thead>
      <tbody>
        {movedLines.map((line) => (
          <tr key={line.orderlineId}>
            <td>{line.productName ?? 'פריט כללי'}</td>
            <td>{`×${line.quantity}`}</td>
            <td>{`₪${Number(line.unitPrice).toFixed(2)}`}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const OrderSnapshotTable: React.FC<{
  before: AdminTransferOrderlineSnapshot[];
  after: AdminTransferOrderlineSnapshot[];
}> = ({ before, after }) => {
  if (before.length === 0 && after.length === 0) {
    return <div className={classes.emptyTab}>{'אין נתוני מצב להצגה.'}</div>;
  }

  const renderRows = (rows: AdminTransferOrderlineSnapshot[], emptyMsg: string) => (
    rows.length === 0
      ? <tr><td colSpan={3} className={classes.snapshotEmpty}>{emptyMsg}</td></tr>
      : rows.map((line) => (
        <tr key={line.orderlineId}>
          <td>{line.productName ?? 'פריט כללי'}</td>
          <td>{`×${line.quantity}`}</td>
          <td>{`₪${Number(line.unitPrice).toFixed(2)}`}</td>
        </tr>
      ))
  );

  return (
    <div className={classes.snapshotGrid}>
      <div className={classes.snapshotSide}>
        <h4 className={classes.snapshotSideTitle}>{'לפני ההעברה'}</h4>
        <table className={classes.itemsTable}>
          <thead>
            <tr>
              <th>{'פריט'}</th>
              <th>{'כמות'}</th>
              <th>{'מחיר'}</th>
            </tr>
          </thead>
          <tbody>{renderRows(before, 'ריק לפני ההעברה')}</tbody>
        </table>
      </div>

      <div className={classes.snapshotDivider} />

      <div className={classes.snapshotSide}>
        <h4 className={classes.snapshotSideTitle}>{'אחרי ההעברה'}</h4>
        <table className={classes.itemsTable}>
          <thead>
            <tr>
              <th>{'פריט'}</th>
              <th>{'כמות'}</th>
              <th>{'מחיר'}</th>
            </tr>
          </thead>
          <tbody>{renderRows(after, 'ריק אחרי ההעברה')}</tbody>
        </table>
      </div>
    </div>
  );
};

export const TransferAuditDetail: React.FC<Props> = ({ row }) => {
  const [activeTab, setActiveTab] = useState<string | null>('items');

  const kindLabel = transferKindLabels[row.transferKind] ?? row.transferKind;
  const sourceTableName = row.sourceOrderBefore[0]?.tableName ?? null;
  const targetTableName = row.targetOrderAfter[0]?.tableName ?? row.targetOrderBefore[0]?.tableName ?? null;

  return (
    <div className={classes.root}>

      {/* Two-column detail grid */}
      <div className={classes.detailsGrid}>
        <div className={classes.detailsSection}>
          <h3 className={classes.sectionTitle}>{'פרטי העברה'}</h3>
          <DetailField label="תאריך העברה" value={formatDateTime(row.createdAt)} />
          <DetailField label="סוג העברה" value={kindLabel} />
          <DetailField label="מלצר" value={row.performedByName} />
          <DetailField label="סשן" value={row.sessionName} />
        </div>

        <div className={classes.detailsSection}>
          <h3 className={classes.sectionTitle}>{'שולחנות'}</h3>
          <DetailField label="שולחן מקור" value={sourceTableName} />
          <DetailField label="שולחן יעד" value={targetTableName} />
          <DetailField label="הזמנת מקור" value={row.sourceOrderCode} />
          <DetailField label="הזמנת יעד" value={row.targetOrderCode} />
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onChange={setActiveTab} className={classes.tabs}>
        <Tabs.List>
          <Tabs.Tab value="items" data-testid="monitoring-transfer-detail-tab-items">{'שורות שהועברו'}</Tabs.Tab>
          <Tabs.Tab value="source" data-testid="monitoring-transfer-detail-tab-source">{'הזמנת מקור לפני / אחרי'}</Tabs.Tab>
          <Tabs.Tab value="target" data-testid="monitoring-transfer-detail-tab-target">{'הזמנת יעד לפני / אחרי'}</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="items" className={classes.tabPanel}>
          <TransferredItemsTable items={row.items} sourceOrderBefore={row.sourceOrderBefore} />
        </Tabs.Panel>

        <Tabs.Panel value="source" className={classes.tabPanel}>
          <OrderSnapshotTable
            before={row.sourceOrderBefore ?? []}
            after={row.sourceOrderAfter ?? []}
          />
        </Tabs.Panel>

        <Tabs.Panel value="target" className={classes.tabPanel}>
          <OrderSnapshotTable
            before={row.targetOrderBefore ?? []}
            after={row.targetOrderAfter ?? []}
          />
        </Tabs.Panel>
      </Tabs>

    </div>
  );
};
